import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { createClient } from '@kairos/supabase/browser'

import { router } from '~/router'
import {
  AuthErrorBanner,
  AuthField,
  AuthSubmit,
  AuthTitle,
} from '~/components/auth-shell/AuthFormPrimitives'
import { GoogleSignInButton } from '~/components/auth/GoogleSignInButton'
import {
  Turnstile,
  getTurnstileSiteKey,
} from '~/components/auth/Turnstile'
import { checkPlatformAdminAccess } from '~/lib/auth/platform-admin-access'

/**
 * `/authentication/login` — admin sign-in entry surface.
 * Google and password sessions pass the same database-backed admin gate.
 * `?challenge=otp` remains a design preview until platform-admin MFA lands.
 */

const searchSchema = z.object({
  returnTo: z.string().optional(),
  challenge: z.enum(['otp']).optional(),
})

const loginSchema = z.object({
  email: z.string().email('Enter a valid email address.'),
  password: z.string().min(1, 'Password is required.'),
})

type LoginInput = z.infer<typeof loginSchema>

const otpSchema = z.object({
  code: z
    .string()
    .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app.'),
})

type OtpInput = z.infer<typeof otpSchema>

export const Route = createFileRoute('/authentication/login')({
  beforeLoad: ({ context, search }) => {
    if (context.auth.isPlatformAdmin === true) {
      throw redirect({ to: search.returnTo ?? '/admin' })
    }
  },
  validateSearch: searchSchema,
  component: LoginPage,
})

type Stage = 'credentials' | 'otp' | 'non-admin'

function LoginPage(): ReactNode {
  const search = Route.useSearch()
  const [stage, setStage] = useState<Stage>(
    search.challenge === 'otp' ? 'otp' : 'credentials',
  )
  const [bannerError, setBannerError] = useState<string | null>(null)
  const [signedInEmail, setSignedInEmail] = useState<string | null>(null)

  return (
    <>
      {stage === 'credentials' ? (
        <CredentialsForm
          bannerError={bannerError}
          returnTo={search.returnTo}
          onError={setBannerError}
          onSuccess={() => {
            setBannerError(null)
            void router.invalidate()
          }}
          onNonAdmin={(email) => {
            setBannerError(null)
            setSignedInEmail(email)
            setStage('non-admin')
          }}
        />
      ) : null}
      {stage === 'otp' ? (
        <OtpForm
          bannerError={bannerError}
          onCancel={() => {
            setBannerError(null)
            setStage('credentials')
          }}
        />
      ) : null}
      {stage === 'non-admin' ? (
        <NonAdminEmptyState
          email={signedInEmail}
          onTryAgain={() => {
            setSignedInEmail(null)
            setStage('credentials')
          }}
        />
      ) : null}
    </>
  )
}

interface CredentialsFormProps {
  readonly bannerError: string | null
  readonly returnTo?: string | undefined
  readonly onError: (message: string | null) => void
  readonly onSuccess: () => void
  readonly onNonAdmin: (email: string) => void
}

// Exported for the route's unit tests; rendered by `LoginPage` above.
export function CredentialsForm({
  bannerError,
  returnTo,
  onError,
  onSuccess,
  onNonAdmin,
}: CredentialsFormProps): ReactNode {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  })

  // CAPTCHA outage hotfix: Supabase CAPTCHA protection is enabled project-wide,
  // so the password grant fails with `captcha protection: request disallowed
  // (no captcha_token found)` unless a solved Turnstile token is forwarded.
  // With no site key configured the widget is absent and nothing changes.
  const turnstileSiteKey = getTurnstileSiteKey()
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  // Remount key: a Turnstile token is single-use, so after GoTrue consumes one
  // (even on a wrong password) the widget must hand out a fresh challenge.
  const [captchaAttempt, setCaptchaAttempt] = useState(0)

  const onSubmit = async (values: LoginInput): Promise<void> => {
    onError(null)

    // Never fire a request that cannot pass: when the widget is rendered the
    // token must exist first, and the failure is surfaced in the error banner
    // rather than silently doing nothing.
    if (turnstileSiteKey && !captchaToken) {
      onError('Complete the verification challenge to continue.')
      return
    }

    const supabase = createClient()

    const { data, error } = await supabase.auth.signInWithPassword({
      email: values.email,
      password: values.password,
      // GoTrue performs the siteverify; the browser only forwards the token.
      ...(captchaToken ? { options: { captchaToken } } : {}),
    })

    if (error || !data.user) {
      if (captchaToken) {
        setCaptchaToken(null)
        setCaptchaAttempt((attempt) => attempt + 1)
      }

      const message = error?.message ?? ''
      onError(
        /captcha/i.test(message)
          ? 'Verification failed. Complete the challenge again and retry.'
          : message || 'Email or password is incorrect.',
      )
      return
    }

    const access = await checkPlatformAdminAccess(supabase, data.user.id)

    if (access.status === 'error') {
      await supabase.auth.signOut({ scope: 'local' })
      onError('We could not verify your admin access. Try again.')
      return
    }

    if (access.status === 'denied') {
      await supabase.auth.signOut({ scope: 'local' })
      onNonAdmin(data.user.email ?? values.email)
      return
    }

    if (access.status === 'mfa_required') {
      // CASA 3.3.1: keep the session and send them to the TOTP challenge/enrol surface
      // rather than signing out (which would loop them back through aal1 sign-in).
      void router.navigate({ to: '/authentication/mfa' })
      return
    }

    onSuccess()
  }

  return (
    <>
      <AuthTitle
        title="Sign in to Admin"
        subtitle="Use your platform admin email."
      />
      {bannerError ? <AuthErrorBanner>{bannerError}</AuthErrorBanner> : null}
      <GoogleSignInButton returnTo={returnTo} onError={onError} />
      <div className="ka-auth-divider" aria-hidden="true">
        <span>or</span>
      </div>
      <form noValidate onSubmit={handleSubmit(onSubmit)} method="post" className="ka-stack">
        <AuthField
          id="login-email"
          label="Email"
          type="email"
          placeholder="you@kairos.com"
          autoComplete="username"
          error={errors.email?.message}
          inputProps={register('email')}
        />
        <div className="ka-auth-password-group">
          <AuthField
            id="login-password"
            label="Password"
            type="password"
            autoComplete="current-password"
            error={errors.password?.message}
            inputProps={register('password')}
          />
          <div className="ka-auth-recovery-row">
            <Link to="/authentication/forgot-password" className="ka-link">
              Forgot password?
            </Link>
          </div>
        </div>
        {turnstileSiteKey ? (
          <Turnstile
            key={captchaAttempt}
            siteKey={turnstileSiteKey}
            onVerify={setCaptchaToken}
            onExpire={() => setCaptchaToken(null)}
            onError={() => setCaptchaToken(null)}
          />
        ) : null}
        <AuthSubmit loading={isSubmitting}>Sign in</AuthSubmit>
      </form>
      <p className="ka-field-helper">
        Don&apos;t have admin access yet? Request access from your platform
        owner.
      </p>
    </>
  )
}

interface OtpFormProps {
  readonly bannerError: string | null
  readonly onCancel: () => void
}

function OtpForm({ bannerError, onCancel }: OtpFormProps): ReactNode {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<OtpInput>({
    resolver: zodResolver(otpSchema),
  })

  // MFA verification is intentionally still a preview.
  const onSubmit = async (_values: OtpInput): Promise<void> => {
    await new Promise((resolve) => setTimeout(resolve, 200))
    throw new Error(
      'OTP verification is not connected to Supabase MFA yet. See route doc.',
    )
  }

  return (
    <>
      <AuthTitle
        title="Verify it's you"
        subtitle="Enter the 6-digit code from your authenticator app."
      />
      {bannerError ? <AuthErrorBanner>{bannerError}</AuthErrorBanner> : null}
      <form noValidate onSubmit={handleSubmit(onSubmit)} method="post" className="ka-stack">
        <AuthField
          id="login-otp"
          label="Authenticator code"
          type="text"
          placeholder="123456"
          autoComplete="one-time-code"
          error={errors.code?.message}
          inputProps={{
            ...register('code'),
            inputMode: 'numeric',
            maxLength: 6,
          }}
        />
        <AuthSubmit loading={isSubmitting}>Verify</AuthSubmit>
      </form>
      <button type="button" onClick={onCancel} className="ka-link">
        Use a backup code
      </button>
    </>
  )
}

interface NonAdminEmptyStateProps {
  readonly email: string | null
  readonly onTryAgain: () => void
}

function NonAdminEmptyState({
  email,
  onTryAgain,
}: NonAdminEmptyStateProps): ReactNode {
  return (
    <>
      <AuthTitle
        title="Required role: platform admin"
        subtitle={
          email
            ? `${email} is signed in to Kairos but doesn't have access to this admin app.`
            : "You're signed in to Kairos but don't have access to this admin app."
        }
      />
      <p className="ka-auth-sub">
        Switch to the customer Dashboard to continue, or sign in with a
        platform admin account.
      </p>
      <a
        href="https://thekairos.app/dashboard"
        className="ka-btn ka-btn--primary ka-btn-block ka-btn--no-underline ka-btn--block-mb-12"
      >
        Open Dashboard
      </a>
      <button
        type="button"
        onClick={onTryAgain}
        className="ka-btn ka-btn--secondary ka-btn-block"
      >
        Sign in with a different account
      </button>
    </>
  )
}
